// Edit a catalogue game's launch recipes (F-CAT-05, spec/10 §5.2): how a station launches it. HQ
// keeps one or two recipes per launcher; the venue library carries them to the station. Each recipe
// has a launcher, a launch type (exe/uri/store/lnk) and the fields that type needs — a `PUT` replaces
// the whole set. The station-side execution is the C# fleet's; this is the server model's editor.
// `RecipeDraftsEditor` is the editor itself, shared with the "Add app" stepper (`AppDialog.tsx`).

import type { Game, LaunchRecipe, LaunchRecipeLauncher, LaunchRecipeType } from '@clavian/api-client';
import { useTranslation } from '@clavian/i18n/react';
import { Button, Dialog, SelectField, TextField, useToast } from '@clavian/ui-web';
import { useState, type ReactNode } from 'react';

import { Notice } from '../../ui';
import { useSetLaunchRecipes } from './queries';
import { useProblem } from '../../api/use-problem';

const LAUNCHERS: readonly LaunchRecipeLauncher[] = ['steam', 'epic', 'ea', 'ubisoft', 'battlenet', 'riot', 'microsoft_store', 'standalone', 'other'];
const TYPES: readonly LaunchRecipeType[] = ['exe', 'uri', 'store', 'lnk'];

/** A recipe as the editor holds it: every field a string, so switching type keeps what was typed. */
export interface RecipeDraft {
  launcher: LaunchRecipeLauncher;
  type: LaunchRecipeType;
  label: string;
  path: string;
  args: string;
  workingDirectory: string;
  uri: string;
  aumid: string;
}

export const BLANK_RECIPE: RecipeDraft = { launcher: 'steam', type: 'uri', label: '', path: '', args: '', workingDirectory: '', uri: '', aumid: '' };

export function draftFrom(recipe: LaunchRecipe): RecipeDraft {
  return {
    launcher: recipe.launcher,
    type: recipe.type,
    label: recipe.label ?? '',
    path: recipe.path ?? '',
    args: recipe.args ?? '',
    workingDirectory: recipe.workingDirectory ?? '',
    uri: recipe.uri ?? '',
    aumid: recipe.aumid ?? '',
  };
}

/** A draft as the contract's `LaunchRecipe`, keeping only the fields its type uses. `null` when a required field is blank. */
export function toRecipe(draft: RecipeDraft): LaunchRecipe | null {
  const label = draft.label.trim() === '' ? {} : { label: draft.label.trim() };
  switch (draft.type) {
    case 'exe': {
      if (draft.path.trim() === '') return null;
      return {
        launcher: draft.launcher,
        type: 'exe',
        path: draft.path.trim(),
        ...(draft.args.trim() === '' ? {} : { args: draft.args.trim() }),
        ...(draft.workingDirectory.trim() === '' ? {} : { workingDirectory: draft.workingDirectory.trim() }),
        ...label,
      };
    }
    case 'lnk':
      return draft.path.trim() === '' ? null : { launcher: draft.launcher, type: 'lnk', path: draft.path.trim(), ...label };
    case 'uri':
      return draft.uri.trim() === '' ? null : { launcher: draft.launcher, type: 'uri', uri: draft.uri.trim(), ...label };
    case 'store':
      return draft.aumid.trim() === '' ? null : { launcher: draft.launcher, type: 'store', aumid: draft.aumid.trim(), ...label };
  }
}

export function LaunchRecipesDialog({ game, open, onOpenChange }: { readonly game: Game; readonly open: boolean; readonly onOpenChange: (open: boolean) => void }): ReactNode {
  const { t } = useTranslation('hq');
  return (
    <Dialog open={open} onOpenChange={onOpenChange} size="lg" dirty="auto" title={t('catalogue.launch.title')} description={game.name}>
      {open ? <LaunchRecipesForm game={game} onDone={() => onOpenChange(false)} /> : null}
    </Dialog>
  );
}

function LaunchRecipesForm({ game, onDone }: { readonly game: Game; readonly onDone: () => void }): ReactNode {
  const { t } = useTranslation('hq');
  const problems = useProblem();
  const { show } = useToast();
  const set = useSetLaunchRecipes();
  const [drafts, setDrafts] = useState<RecipeDraft[]>(() => game.launchRecipes.map(draftFrom));
  const [error, setError] = useState<string | undefined>(undefined);

  const submit = () => {
    const recipes: LaunchRecipe[] = [];
    for (const draft of drafts) {
      const recipe = toRecipe(draft);
      if (recipe === null) {
        setError(t('catalogue.launch.incomplete'));
        return;
      }
      recipes.push(recipe);
    }
    setError(undefined);
    set.mutate(
      { id: game.id, body: { recipes } },
      {
        onSuccess: () => {
          show({ tone: 'ok', title: t('catalogue.launch.saved', { game: game.name }) });
          onDone();
        },
        onError: (err) => setError(problems.text(err, { fallback: t('catalogue.launch.failed') })),
      },
    );
  };

  return (
    <div className="console-form">
      <p className="console-muted">{t('catalogue.launch.intro')}</p>
      {error !== undefined ? <Notice tone="danger" role="alert">{error}</Notice> : null}

      <RecipeDraftsEditor drafts={drafts} onChange={setDrafts} />

      <div className="console-form__actions">
        <span style={{ flex: 1 }} />
        <Button type="button" variant="ghost" onClick={onDone}>
          {t('catalogue.launch.cancel')}
        </Button>
        <Button type="button" variant="primary" busy={set.isPending} onClick={submit}>
          {t('catalogue.launch.save')}
        </Button>
      </div>
    </div>
  );
}

/** The recipe list editor: one fieldset per recipe with the fields its type needs, and "Add a recipe". */
export function RecipeDraftsEditor({ drafts, onChange }: { readonly drafts: readonly RecipeDraft[]; readonly onChange: (drafts: RecipeDraft[]) => void }): ReactNode {
  const { t } = useTranslation('hq');
  const update = (index: number, patch: Partial<RecipeDraft>) => onChange(drafts.map((draft, i) => (i === index ? { ...draft, ...patch } : draft)));
  const remove = (index: number) => onChange(drafts.filter((_, i) => i !== index));

  return (
    <>
      {drafts.length === 0 ? <p className="console-muted">{t('catalogue.launch.empty')}</p> : null}

      {drafts.map((draft, index) => (
        <fieldset key={index} className="console-hq-fieldset">
          <div className="console-form__row">
            <SelectField
              label={t('catalogue.launch.launcher')}
              options={LAUNCHERS.map((launcher) => ({ value: launcher, label: t(`catalogue.launch.launcherName.${launcher}`) }))}
              value={draft.launcher}
              onValueChange={(value) => update(index, { launcher: value as LaunchRecipeLauncher })}
            />
            <SelectField
              label={t('catalogue.launch.type')}
              options={TYPES.map((type) => ({ value: type, label: t(`catalogue.launch.typeName.${type}`) }))}
              value={draft.type}
              onValueChange={(value) => update(index, { type: value as LaunchRecipeType })}
            />
          </div>
          <TextField label={t('catalogue.launch.label')} showOptional value={draft.label} onChange={(event) => update(index, { label: event.target.value })} />

          {draft.type === 'exe' || draft.type === 'lnk' ? (
            <TextField
              label={draft.type === 'lnk' ? t('catalogue.launch.lnkPath') : t('catalogue.launch.path')}
              description={t('catalogue.launch.pathHint')}
              required
              value={draft.path}
              onChange={(event) => update(index, { path: event.target.value })}
            />
          ) : null}
          {draft.type === 'exe' ? (
            <div className="console-form__row">
              <TextField label={t('catalogue.launch.args')} showOptional value={draft.args} onChange={(event) => update(index, { args: event.target.value })} />
              <TextField label={t('catalogue.launch.workingDirectory')} showOptional value={draft.workingDirectory} onChange={(event) => update(index, { workingDirectory: event.target.value })} />
            </div>
          ) : null}
          {draft.type === 'uri' ? (
            <TextField label={t('catalogue.launch.uri')} description={t('catalogue.launch.uriHint')} required value={draft.uri} onChange={(event) => update(index, { uri: event.target.value })} />
          ) : null}
          {draft.type === 'store' ? (
            <TextField label={t('catalogue.launch.aumid')} description={t('catalogue.launch.aumidHint')} required value={draft.aumid} onChange={(event) => update(index, { aumid: event.target.value })} />
          ) : null}

          <div className="console-form__actions console-form__actions--end">
            <Button type="button" variant="ghost" size="sm" onClick={() => remove(index)}>
              {t('catalogue.launch.remove')}
            </Button>
          </div>
        </fieldset>
      ))}

      <div className="console-form__actions">
        <Button type="button" variant="secondary" onClick={() => onChange([...drafts, { ...BLANK_RECIPE }])}>
          {t('catalogue.launch.add')}
        </Button>
      </div>
    </>
  );
}
